import { nextTick, px, sleep } from '@front/utils';
import { createComponent, getRef } from '@front/utils/alpine';
import algoliasearch from 'algoliasearch/lite';
import instantsearch, { BaseHit, Hit, InstantSearch } from 'instantsearch.js';

import { connectSearchBox, connectHits, connectStats, connectRefinementList, connectRange, connectClearRefinements } from 'instantsearch.js/es/connectors';
import { debounce, get } from 'lodash-es';

import product, { TProduct, RLXProduct } from '@front/templates/product';
import prestashop from 'prestashop';
import { Alpine } from 'alpinejs';

const currencyId = prestashop.currency.id as string;
const iso_code   = prestashop.language.iso_code as string;
const langAttr   = (item: TBaseProduct, path: string) => get(item, `langs.${iso_code}.${path}`, get(item, `langs.fr.${path}`));

enum EStaticDOM {
    FACETS_WRAPPER = 'static_search_wrapper',
}

type TSearchBoxConnector = { input: HTMLInputElement };
const customSearchBox = connectSearchBox<TSearchBoxConnector>(({ query, refine, widgetParams: { input } }, isFirstRender) => {
    if (!isFirstRender) {
        return;
    }

    input.addEventListener('input', event => refine((event.target as HTMLInputElement).value));
});

type THitsConnector = { container: HTMLElement };
const customHits = connectHits<THitsConnector>(({ hits, widgetParams: { container } }) => {
    container.innerHTML = (hits as Hit<TProduct>[]).map(product).join('');
});

type THitsCountStatsConnector = { container: HTMLElement };
const customHitsCountStats = connectStats<THitsCountStatsConnector>(({ nbHits, widgetParams: { container } }) => {
    container.innerHTML = nbHits.toString();
});

type TClearRefinements = { el: HTMLElement };
const customClearRefinements = connectClearRefinements<TClearRefinements>(({ refine, widgetParams: { el } }, isFirstRender) => {
    if (!isFirstRender) {
        return;
    }

    el.addEventListener('click', () => refine());
});

type TAggregateConfig = { name: string, type: 'checkboxes' | 'range', field: string, [key: string]: unknown }
function customRefinement (this: Alpine, aggregate: TAggregateConfig) {
    const { name, type, field: attribute } = aggregate;

    const checkboxes = connectRefinementList(({ refine, canRefine, items, widgetParams: { attribute }}) => {
        items && canRefine ? this.refinements.push({ name, items, refine, attribute, type }) : null;
    });

    const range = connectRange<{ aggregate: TAggregateConfig }>(({ refine, canRefine, range, start, widgetParams: { aggregate }}) => {
        const { unit, step } = aggregate;
        const { min, max } = range;

        canRefine ? this.refinements.push({ name, refine, attribute, type, opts: {
            from: Number.isFinite(start[0]) ? start[0] : min,
            to: Number.isFinite(start[1]) ? start[1] : max,
            min,
            max,
            unit,
            step,
        }}) : null;
    });

    return { checkboxes, range }[type]({ sortBy: ['name:asc'], attribute, aggregate });
}

type TBaseProduct = {
    formatted_price: string;
    percent: number;
    price: number;
    base_price: number;
    manufacturer: {
        name: string;
    };
    prices: {
        [key: string]: string;
    };
    langs: {
        [key: string]: {
            [key: string]: object | string | number;
        };
    };
};

type TMyHit = TBaseProduct & TProduct & BaseHit;
function transformItems (items: Hit<BaseHit>[]) {
    return (items as Hit<TMyHit>[]).map(item => ({
        ...item,
        image_url: langAttr(item, 'image_url'),
        name: langAttr(item, 'name'),
        manufacturer_name: item.manufacturer && typeof item.manufacturer.name !== 'undefined'  ? item.manufacturer.name : '',
        url: langAttr(item, 'url'),
        price: item.formatted_price,
        has_discount: item.reduc != 0,
        discount_type: item.reduc && item.percent > 0 ? 'percentage' : item.reduc ? 'amout' : '',
        discount_percentage: item.percent,
        discount_amount: Math.round((item.price - item.base_price) / item.base_price * 100),
        price_amount: item.price,
        price_without_reduction: item.base_price,
        flags: Object.values(langAttr(item, 'flags')),
    }));
}

type RLXMyHit = TBaseProduct & RLXProduct & BaseHit;
function transformRolexItems (items: Hit<BaseHit>[]) {
    return (items as Hit<RLXMyHit>[]).map(item => ({
        ...item,
        image_url: item.image_url,
        name: langAttr(item, 'name'),
        title: langAttr(item, 'title'),
        manufacturer_name: 'Rolex',
        url: langAttr(item, 'url'),
        price: item.prices && typeof item.prices[currencyId] !== 'undefined' && item.prices[currencyId] ? item.prices[currencyId] : langAttr(item, 'no_price'),
    }));
}


function setSearchPosition (ctx: Alpine) {
    const top = ctx.$el.getBoundingClientRect().top + ctx.$el.clientHeight;
    ctx.$refs.wrapper.style.top = px(top);
    ctx.$refs.wrapper.style.height = `calc(100vh - ${px(top)})`;
}

function toggleBodyOverflow (force: boolean) {
    document.documentElement.classList.toggle('overflow-y-hidden', force);
}

export default createComponent(() => ({
    search: null as InstantSearch | null,
    hasQuery: false,
    hasResults: true,
    isOpened: false,
    isFiltersOpened: false,
    refinements: [],
    renderedRefinements: [],
    refinementsCount: 0,
    openedRefinements: {},
    rolexSearch: true,
    viewSort: false,
    viewFacets: false,
    showNoResultLabel: false,

    open () {
        this.isOpened = true;
        toggleBodyOverflow(true);
        sleep(150).then(() => this.$refs.input.focus());
        nextTick(() => setSearchPosition(this));
    },

    close () {
        this.isOpened = false;
        this.hasResults = true;
        this.refinementsCount = 0;
        this.refinements = [];
        this.renderedRefinements = [];
        this.openedRefinements = {};
        this.$refs.input.value = null;
        this.$refs.input.dispatchEvent(new Event('input', { bubbles: true }));
        this.$refs.reset.dispatchEvent(new Event('click', { bubbles: true }));
        toggleBodyOverflow(false);
    },

    toggleFilters (force: boolean) {
        this.isFiltersOpened = force == undefined ? !this.isFiltersOpened : force;
    },

    isRefinementOpen (key: number) {
        return this.openedRefinements[key] ?? false;
    },

    toggleRefinement (key: number, force: boolean) {
        const current = this.openedRefinements[key] ?? false;
        this.openedRefinements[key] = force == undefined ? !current : force
    },

    init () {
        const ctx = this;
        const configMeta = document.querySelector<HTMLMetaElement>('meta[name="algolia-config"]');

        if (configMeta == null) {
            throw 'Meta tag name="algolia-config" not found';
        }

        nextTick(() => setSearchPosition(ctx));
        window.addEventListener('resize', debounce(() => setSearchPosition(ctx), 100));

        this.$watch('hasQuery', () => toggleBodyOverflow(this.hasQuery));

        this.$watch('isFiltersOpened', async () => {
            const facetsWrapperEl = getRef(ctx, EStaticDOM.FACETS_WRAPPER) as HTMLElement;

            if (this.isOpened) {
                return facetsWrapperEl.classList.remove('overflow-x-hidden');
            }

            await sleep(parseFloat(getComputedStyle(facetsWrapperEl)['transitionDuration']) * 1000);
            facetsWrapperEl.classList.add('overflow-x-hidden');
        });

        this.initSearch(configMeta);
    },

    initSearch(configMeta: HTMLMetaElement) {
        const ctx = this;
        const { appId, apiKey, indexLepage, indexRolex, aggregates, rolexAggregates, sorts, rolexSorts } = JSON.parse(configMeta.content);

        let timeoutId: null | ReturnType<typeof setTimeout> = null;
        const delay = 250;

        this.search = instantsearch({
            indexName: indexRolex,
            searchClient: algoliasearch(appId, apiKey),
            searchFunction (helper) {
                if(timeoutId)
                    clearTimeout(timeoutId);

                timeoutId = setTimeout(() => {
                    ctx.refinements = [];
                    ctx.hasQuery = helper.state.query;
                    if (!ctx.hasQuery) {
                        return;
                    }
                    helper.search();
                }, delay);
            }
        }).addWidgets([
            ...(rolexAggregates as TAggregateConfig[]).map(customRefinement.bind(ctx)),
            customSearchBox({ input: this.$refs.input }),
            customHitsCountStats({ container: this.$refs.nbHitsTopbar }),
            customHitsCountStats({ container: this.$refs.nbHitsSidebar }),
            customClearRefinements({ el: this.$refs.reset }),
            customHits({
                container: this.$refs.results,
                transformItems: transformRolexItems,
            }),
        ]).on('render', () => {
            const helper = ctx.search.helper;
            ctx.renderedRefinements = [...ctx.refinements];
            ctx.hasResults = ctx.search.helper.lastResults.nbHits !== 0;
            ctx.refinementsCount = Object.values<unknown[]>(helper.state.disjunctiveFacetsRefinements).reduce((prev, curr) => {
                return prev + curr.length;
            }, 0);

            if(! ctx.hasResults) {
                ctx.searchOnLepageProducts(indexLepage, configMeta);
            }
        });

        this.initSortBy(configMeta);

        this.search.start();

        this.returnToRolexOnQueryEvent(configMeta);
    },

    returnToRolexOnQueryEvent(configMeta: HTMLMetaElement) {
        const otherKeys = ['Backspace', 'Delete'];
        const ctx = this;
        this.$refs.input.addEventListener('keydown', (event: KeyboardEvent) => {
            if(event.key.length == 1 || otherKeys.indexOf(event.key) >= 0) {
                if(!ctx.rolexSearch) {
                    ctx.rolexSearch = true;
                    ctx.searchOnRolexProducts(configMeta);
                }
            }
        });
    },

    initSortBy(configMeta: HTMLMetaElement) {
        const ctx = this;
        const { appId, apiKey, indexLepage, indexRolex, aggregates, rolexAggregates, sorts, rolexSorts } = JSON.parse(configMeta.content);

        sorts.forEach((option: {label: string, value: string}) => {
            this.$refs.sortBy.innerHTML += '<option value="' + option.value + '">' + option.label + '</option>';
        });

        this.$refs.sortBy.addEventListener('change', (event: Event) => {
            event.stopPropagation();
            if(!ctx.rolexSearch) {
                const select = event.target as HTMLSelectElement;
                const indexName = select.value;
                ctx.search.helper.setIndex(indexName).search();
            }
        });
    },

    searchOnRolexProducts(configMeta: HTMLMetaElement) {
        // get rolex results
        const ctx = this;
        ctx.viewSort = ctx.viewFacets = false;
        const { appId, apiKey, indexLepage, indexRolex, aggregates, rolexAggregates, sorts, rolexSorts } = JSON.parse(configMeta.content);

        this.search.helper.setIndex(indexRolex);

        const widgets = this.search.mainIndex.getWidgets();
        this.search.removeWidgets(widgets);

        this.search._events.render = () => {
            const helper = ctx.search.helper;
            ctx.renderedRefinements = [...ctx.refinements];
            ctx.hasResults = ctx.search.helper.lastResults.nbHits !== 0;
            ctx.refinementsCount = Object.values<unknown[]>(helper.state.disjunctiveFacetsRefinements).reduce((prev, curr) => {
                return prev + curr.length;
            }, 0);

            if(! ctx.hasResults) {
                ctx.searchOnLepageProducts(indexLepage, configMeta);
            }
        };

        // get rolex results
        this.search.addWidgets([
            ...(aggregates as TAggregateConfig[]).map(customRefinement.bind(ctx)),
            customSearchBox({ input: this.$refs.input }),
            customHitsCountStats({ container: this.$refs.nbHitsTopbar }),
            customHitsCountStats({ container: this.$refs.nbHitsSidebar }),
            customClearRefinements({ el: this.$refs.reset }),
            customHits({
                container: this.$refs.results,
                transformItems: transformRolexItems,
            }),
        ]);

        this.search.refresh();

        this.$refs.input.dispatchEvent(new Event('input', { bubbles: true }));
    },

    searchOnLepageProducts(indexName: string, configMeta: HTMLMetaElement) {
        // if no result on Rolex get Lepage results
        const ctx = this;
        ctx.viewSort = ctx.viewFacets = true;

        const { appId, apiKey, indexLepage, indexRolex, aggregates, rolexAggregates, sorts, rolexSorts } = JSON.parse(configMeta.content);

        // change instantsearch content
        this.search.helper.setIndex(indexName);
        const widgets = this.search.mainIndex.getWidgets();
        this.search.removeWidgets(widgets);

        this.search._events.render = () => {
            const helper = ctx.search.helper;
            ctx.renderedRefinements = [...ctx.refinements];
            ctx.hasResults = ctx.search.helper.lastResults.nbHits !== 0;
            ctx.refinementsCount = Object.values<unknown[]>(helper.state.disjunctiveFacetsRefinements).reduce((prev, curr) => {
                return prev + curr.length;
            }, 0);

            if(!ctx.hasResults)
                ctx.showNoResultLabel = true;
            else
                ctx.showNoResultLabel = false;
        };

        this.search.addWidgets([
            ...(aggregates as TAggregateConfig[]).map(customRefinement.bind(ctx)),
            customSearchBox({ input: this.$refs.input }),
            customHitsCountStats({ container: this.$refs.nbHitsTopbar }),
            customHitsCountStats({ container: this.$refs.nbHitsSidebar }),
            customClearRefinements({ el: this.$refs.reset }),
            customHits({
                container: this.$refs.results,
                transformItems,
            }),
        ]);

        this.search.refresh();

        // trigger query change
        this.$refs.input.dispatchEvent(new Event('input', { bubbles: true }));

        ctx.rolexSearch = false;

    },

}));
